import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import CustomDateTimePicker from "./CustomDateTimePicker";

const ContractExpiryModal = ({
  isOpen,
  onClose,
  onSave,
  initialDate = null,
  playerName = "",
  isSaving = false,
}) => {
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setSelectedDate(initialDate);
      setError("");
    }
  }, [isOpen, initialDate]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (!isSaving) {
          onClose();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isSaving, onClose]);

  const handleSave = () => {
    if (!selectedDate) {
      setError("Please select a contract expiry date");
      return;
    }

    // Validate that the date is in the future
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDateTime = new Date(selectedDate);
    selectedDateTime.setHours(0, 0, 0, 0);

    if (selectedDateTime < today) {
      setError("Contract expiry date must be in the future");
      return;
    }

    onSave(selectedDate);
  };

  const handleCancel = () => {
    if (!isSaving) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  const portalTarget = document.getElementById("modal-root") || document.body;

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50" onClick={handleCancel} />
      <div className="relative flex min-h-full items-end justify-center p-4 md:items-center">
        <div className="w-full max-w-xl rounded-t-2xl bg-white p-6 shadow-xl md:rounded-2xl">
          {/* Header */}
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-[#657084]">
                {initialDate ? "Edit" : "Add"} Contract Expiry
              </p>
              <h2 className="text-xl font-bold text-[#1A212F]">
                {playerName || "Player Contract"}
              </h2>
            </div>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-600"
              onClick={handleCancel}
              disabled={isSaving}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 5L5 15M5 5L15 15"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {/* Date Picker */}
          <div className="mb-6">
            <CustomDateTimePicker
              value={selectedDate}
              onChange={(date) => {
                setSelectedDate(date);
                setError("");
              }}
              label="Contract Expiry Date"
              placeholder="Select contract expiry date"
              required
              error={error}
              disabled={isSaving}
              showTimeSelect={false}
              minDate={new Date()}
              dateFormat="dd/MM/yyyy"

              position="-top-0"
            />
          </div>

          {/* Helper Text */}
          <div className="mb-6 rounded-lg bg-blue-50 p-3">
            <p className="text-sm text-blue-800">
              Select the date when this player's contract with the club will
              expire.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex flex-col gap-3 md:flex-row md:justify-end">
            <button
              type="button"
              onClick={handleCancel}
              className={`w-full rounded-lg border border-[#E6E6E6] px-4 py-2 text-sm font-medium text-[#1E1E1E] transition-colors hover:bg-gray-50 md:w-auto ${
                isSaving ? "cursor-not-allowed opacity-60" : ""
              }`}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!selectedDate || isSaving}
              className={`w-full rounded-lg bg-[#2283FF] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-600 md:w-auto ${
                !selectedDate || isSaving
                  ? "cursor-not-allowed opacity-60 hover:bg-[#2283FF]"
                  : ""
              }`}
            >
              {isSaving ? "Saving..." : "Save Contract Date"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    portalTarget
  );
};

export default ContractExpiryModal;
